import { useEffect } from 'react';

interface PageMetaProps {
  title: string;
  description: string;
  canonicalPath?: string;
  imagePath?: string;
  noIndex?: boolean;
  pageType?: 'WebPage' | 'CollectionPage' | 'ProfilePage';
}

const SITE_ORIGIN = 'https://planetearth.help';

function ensureMeta(selector: string, attribute: 'name' | 'property', value: string) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, selector.match(/["']([^"']+)["']/)?.[1] || '');
    document.head.appendChild(element);
  }
  element.content = value;
}

export function PageMeta({
  title,
  description,
  canonicalPath,
  imagePath = '/og-image.png',
  noIndex = false,
  pageType = 'WebPage',
}: PageMetaProps) {
  useEffect(() => {
    const resolvedPath = canonicalPath || window.location.pathname || '/';
    const canonicalUrl = new URL(resolvedPath, SITE_ORIGIN).toString();
    const imageUrl = new URL(imagePath, SITE_ORIGIN).toString();
    let canonicalElement = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

    if (!canonicalElement) {
      canonicalElement = document.createElement('link');
      canonicalElement.rel = 'canonical';
      document.head.appendChild(canonicalElement);
    }

    document.title = title;
    canonicalElement.href = canonicalUrl;
    ensureMeta('meta[name="description"]', 'name', description);
    ensureMeta('meta[name="robots"]', 'name', noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');
    ensureMeta('meta[property="og:type"]', 'property', 'website');
    ensureMeta('meta[property="og:site_name"]', 'property', 'Planet Earth Help');
    ensureMeta('meta[property="og:title"]', 'property', title);
    ensureMeta('meta[property="og:description"]', 'property', description);
    ensureMeta('meta[property="og:url"]', 'property', canonicalUrl);
    ensureMeta('meta[property="og:image"]', 'property', imageUrl);
    ensureMeta('meta[name="twitter:card"]', 'name', 'summary_large_image');
    ensureMeta('meta[name="twitter:title"]', 'name', title);
    ensureMeta('meta[name="twitter:description"]', 'name', description);
    ensureMeta('meta[name="twitter:image"]', 'name', imageUrl);

    const structuredData = {
      '@context': 'https://schema.org',
      '@type': pageType,
      name: title,
      description,
      url: canonicalUrl,
      isPartOf: {
        '@type': 'WebSite',
        name: 'Planet Earth Help',
        url: `${SITE_ORIGIN}/`,
      },
      inLanguage: document.documentElement.lang || 'en',
    };
    let structuredDataElement = document.head.querySelector<HTMLScriptElement>('script[data-peh-page-meta]');
    if (!structuredDataElement) {
      structuredDataElement = document.createElement('script');
      structuredDataElement.type = 'application/ld+json';
      structuredDataElement.dataset.pehPageMeta = 'true';
      document.head.appendChild(structuredDataElement);
    }
    structuredDataElement.textContent = JSON.stringify(structuredData).replace(/</gu, '\\u003c');

    return () => {
      structuredDataElement?.remove();
    };
  }, [canonicalPath, description, imagePath, noIndex, pageType, title]);
  return null;
}
